import type {
  SceneActionItem,
  SceneChoice,
  SceneListItem,
  SceneMapEntry,
  SceneNode,
} from "@chuzi/shared/types";
import { createDefaultLine } from "../components/generator-timeline";
import type { TextLine } from "@chuzi/shared/types";

type RawSceneNode = SceneNode & {
  id?: string;
  name?: string;
  order?: number;
};

export function sortScenesForFlow(sceneList: SceneListItem[]): SceneListItem[] {
  return [...sceneList].sort((a, b) => {
    if (a.is_title !== b.is_title) return a.is_title ? -1 : 1;
    if (a.order !== b.order) return a.order - b.order;
    return a.created_at.localeCompare(b.created_at);
  });
}

function isTextLine(line: TextLine): boolean {
  return !line.type || line.type === "text";
}

/** Text-only html for legacy `text_content.html` (never include effect filenames). */
export function composeTextContentHtml(lines: TextLine[]): string {
  return lines.filter(isTextLine).map((l) => l.html).join("");
}

/** Viewer text lines — API payload only (never editor localStorage cache). */
export function resolveSceneTextLines(scene: SceneListItem): TextLine[] {
  const loaded = scene.text_content?.lines;
  if (loaded && loaded.length > 0) return loaded;

  const line = createDefaultLine();
  if (scene.text_content?.html) {
    line.html = scene.text_content.html;
  }
  return [line];
}

export function lineHasPlayablePayload(line: TextLine): boolean {
  if (line.type === "sound" || line.type === "image") {
    return Boolean(line.media_id);
  }
  return line.html.replace(/<[^>]*>/g, "").trim().length > 0;
}

export function sceneHasPlayableLines(lines: TextLine[]): boolean {
  return lines.some(lineHasPlayablePayload);
}

/** Editor preview uses live lines; viewer uses API-backed scene fields. */
export function sceneShowsDefaultSlateWithLines(
  scene: SceneListItem,
  lines: TextLine[],
): boolean {
  return scene.is_title && !scene.media_id && !sceneHasPlayableLines(lines);
}

export function isTransmissionImageUrl(url: string): boolean {
  try {
    const path = new URL(url).pathname.toLowerCase();
    return /\.(png|jpe?g|gif|webp|avif|svg)$/.test(path);
  } catch {
    return /\.(png|jpe?g|gif|webp|avif|svg)(\?|$)/i.test(url);
  }
}

export type TransmissionStageKind = "slate" | "film" | "image" | "composed";

export function resolveTransmissionStageKind(
  scene: SceneListItem,
  lines: TextLine[],
  primaryMediaUrl: string | null,
): TransmissionStageKind {
  if (sceneShowsDefaultSlateWithLines(scene, lines)) return "slate";

  const hasComposedContent = sceneHasPlayableLines(lines);
  const isFilmScene =
    scene.media_mode === "film" ||
    (primaryMediaUrl != null &&
      !isTransmissionImageUrl(primaryMediaUrl) &&
      !hasComposedContent);
  if (isFilmScene && primaryMediaUrl) return "film";
  if (
    primaryMediaUrl &&
    isTransmissionImageUrl(primaryMediaUrl) &&
    !hasComposedContent
  ) {
    return "image";
  }
  return "composed";
}

export const DEFAULT_SLATE_DURATION_MS = 3200;

function choiceFromActionProperties(
  id: string,
  props: Record<string, unknown>,
  name?: string,
): SceneChoice | null {
  const targetSceneId = String(props.target_scene_id ?? "");
  if (!targetSceneId) return null;

  return {
    id,
    label: String(props.label ?? name ?? ""),
    choice_type: "default",
    choice_icon: null,
    choice_icon_media_id: null,
    choice_icon_url: null,
    reveal_mode: "immediate",
    start_time_seconds:
      typeof props.start_time_seconds === "number" ? props.start_time_seconds : null,
    pause_for_choice:
      typeof props.pause_for_choice === "boolean" ? props.pause_for_choice : true,
    end_time_seconds:
      typeof props.end_time_seconds === "number" ? props.end_time_seconds : null,
    target_scene_id: targetSceneId,
    x: Number(props.x ?? 40),
    y: Number(props.y ?? 45),
    w: Number(props.w ?? 12),
    h: Number(props.h ?? 14),
    arrow_rotation: Number(props.arrow_rotation ?? 0),
    arrow_scale: Number(props.arrow_scale ?? 1),
    fade_in_ms: typeof props.fade_in_ms === "number" ? props.fade_in_ms : null,
    fade_out_ms: typeof props.fade_out_ms === "number" ? props.fade_out_ms : null,
    duration_ms: typeof props.duration_ms === "number" ? props.duration_ms : null,
    visibility_rules: null,
    state_updates: [],
  };
}

function choicesFromNodes(nodes: SceneNode[]): SceneChoice[] {
  return (nodes as RawSceneNode[])
    .filter((node) => node.type === "choice")
    .map((node, index) => {
      const props = (node.properties ?? {}) as Record<string, unknown>;
      return choiceFromActionProperties(
        node.id ?? `choice-${index}`,
        props,
        node.name,
      );
    })
    .filter((choice): choice is SceneChoice => choice !== null);
}

/** Editor scene-actions are the source of truth for span/bearing and layout. */
export function choicesFromSceneActions(actions: SceneActionItem[]): SceneChoice[] {
  return actions
    .filter((action) => action.type === "choice")
    .map((action) => {
      const props = (action.properties ?? {}) as Record<string, unknown>;
      return choiceFromActionProperties(action.id, props, action.name);
    })
    .filter((choice): choice is SceneChoice => choice !== null);
}

function withArrowDefaults(choice: SceneChoice): SceneChoice {
  return {
    ...choice,
    arrow_rotation: choice.arrow_rotation ?? 0,
    arrow_scale: choice.arrow_scale ?? 1,
  };
}

/** Choices may live as scene-action nodes (editor) or legacy scene_choices rows. */
export function resolveSceneChoices(entry: SceneMapEntry | null): SceneChoice[] {
  if (!entry) return [];

  const fromNodes = choicesFromNodes(entry.nodes);
  if (fromNodes.length > 0) return fromNodes;

  return entry.choices
    .map(withArrowDefaults)
    .filter((choice) => choice.target_scene_id.length > 0);
}

function sceneLinkTarget(scene: SceneListItem): string | null {
  const raw = scene as SceneListItem & { choice_scene_id?: string | null };
  return raw.choice_scene_id ?? scene.goto_scene_id ?? null;
}

export function resolveAdvanceTarget(
  currentScene: SceneListItem,
  allScenes: SceneListItem[],
  entry: SceneMapEntry | null,
): string | null {
  const sorted = sortScenesForFlow(allScenes);
  const gotoFromScene = sceneLinkTarget(currentScene);
  const gotoFromMap = entry?.goto_scene_id ?? null;
  let targetId = gotoFromScene ?? gotoFromMap;

  if (!targetId || targetId === currentScene.id) {
    const idx = sorted.findIndex((s) => s.id === currentScene.id);
    if (idx >= 0 && idx < sorted.length - 1) {
      return sorted[idx + 1].id;
    }
    return null;
  }

  return targetId;
}

/** Single non-pausing continue choice — auto-advance without a click. */
export function resolveAutoContinueTarget(choices: SceneChoice[]): string | null {
  const candidates = choices.filter(
    (choice) =>
      choice.target_scene_id &&
      !isViewerExitChoice(choice) &&
      !isViewerExitSuppressionMarker(choice) &&
      choice.pause_for_choice === false,
  );
  if (candidates.length !== 1) return null;
  return candidates[0].target_scene_id;
}

/** Synthetic exit choice injected for the terminal scene in StoryViewer. */
export const VIEWER_EXIT_CHOICE_ID = "__chuzi_viewer_exit__";

/** Persisted marker — user removed the terminal exit choice. */
export const VIEWER_EXIT_SUPPRESSED_ID = "__chuzi_viewer_exit_suppressed__";

const VIEWER_EXIT_CHOICE_DEFAULTS = {
  x: 82,
  y: 78,
  w: 12,
  h: 14,
  arrow_rotation: 90,
  arrow_scale: 1,
} as const;

const VIEWER_EXIT_APPEAR_SEC = 3;

export function isViewerExitChoice(choice: SceneChoice): boolean {
  return (
    choice.id === VIEWER_EXIT_CHOICE_ID ||
    choice.target_scene_id === VIEWER_EXIT_CHOICE_ID
  );
}

export function isViewerExitSuppressionMarker(choice: SceneChoice): boolean {
  return choice.target_scene_id === VIEWER_EXIT_SUPPRESSED_ID;
}

/** Hide suppression markers from lists and preview overlays. */
export function visibleSceneChoices(choices: SceneChoice[]): SceneChoice[] {
  return choices.filter((choice) => !isViewerExitSuppressionMarker(choice));
}

/** Stored choices plus synthetic terminal exit when applicable. */
export function resolveEditorPreviewChoices(
  scene: SceneListItem,
  allScenes: SceneListItem[],
  entry: SceneMapEntry | null,
  storedChoices: SceneChoice[],
): SceneChoice[] {
  const visible = visibleSceneChoices(storedChoices);
  if (visible.some((choice) => choice.target_scene_id === VIEWER_EXIT_CHOICE_ID)) {
    return visible;
  }
  const exitChoice = resolveViewerExitChoice(scene, allScenes, entry, storedChoices);
  if (!exitChoice) return visible;
  return [...visible, exitChoice];
}

export function isTerminalScene(
  currentScene: SceneListItem,
  allScenes: SceneListItem[],
  entry: SceneMapEntry | null,
): boolean {
  return resolveAdvanceTarget(currentScene, allScenes, entry) === null;
}

/**
 * Terminal scenes need a pausing exit choice so playback halts instead of
 * auto-ending. Returns null when the scene already has a halting choice.
 */
export function resolveViewerExitChoice(
  currentScene: SceneListItem,
  allScenes: SceneListItem[],
  entry: SceneMapEntry | null,
  existingChoices: SceneChoice[],
): SceneChoice | null {
  if (!isTerminalScene(currentScene, allScenes, entry)) return null;
  if (existingChoices.some(isViewerExitSuppressionMarker)) return null;
  if (
    existingChoices.some(
      (choice) =>
        choice.target_scene_id === VIEWER_EXIT_CHOICE_ID &&
        !isViewerExitSuppressionMarker(choice),
    )
  ) {
    return null;
  }
  if (
    existingChoices.some(
      (choice) =>
        choice.pause_for_choice !== false &&
        !isViewerExitSuppressionMarker(choice) &&
        !isViewerExitChoice(choice),
    )
  ) {
    return null;
  }

  const sceneDurationSec = currentScene.choice_end_time_seconds ?? 5;

  return {
    id: VIEWER_EXIT_CHOICE_ID,
    label: "Go To The End",
    choice_type: "default",
    choice_icon: null,
    choice_icon_media_id: null,
    choice_icon_url: null,
    reveal_mode: "immediate",
    start_time_seconds: VIEWER_EXIT_APPEAR_SEC,
    pause_for_choice: true,
    end_time_seconds: sceneDurationSec + 1,
    target_scene_id: VIEWER_EXIT_CHOICE_ID,
    ...VIEWER_EXIT_CHOICE_DEFAULTS,
    fade_in_ms: 600,
    fade_out_ms: 0,
    duration_ms: 2000,
    visibility_rules: null,
    state_updates: [],
  };
}

/** True when the scene has saved media or non-empty text from the API. */
export function sceneHasPlayableContent(scene: SceneListItem): boolean {
  if (scene.media_id) return true;

  const apiLines = scene.text_content?.lines;
  if (apiLines && apiLines.length > 0) {
    return sceneHasPlayableLines(apiLines);
  }

  const legacyHtml = scene.text_content?.html?.replace(/<[^>]*>/g, "").trim();
  return Boolean(legacyHtml);
}

/** Title scenes without media show the coverbox slate (matches hover HUD). */
export function sceneShowsDefaultSlate(scene: SceneListItem): boolean {
  return sceneShowsDefaultSlateWithLines(scene, resolveSceneTextLines(scene));
}
